import './BlogPost.scss';

import type {
  HowDoesItWork,
  InfoBannerBlock,
  PlanCardsBlock,
  Post,
  RichTextBlock,
  TestimonialItem,
} from '@travelwin/cms/types';
import { Country, formatDate, ProductOffering } from '@travelwin/core';
import { useTranslation } from 'react-i18next';

import { LandingBlogPosts } from '@/pages/Landing/components/LandingBlogPosts';
import { generateHeadingInfo, HeadingInfo } from '~/helpers/generateHeadingId';
import { Node } from '~/types';

import { PageBlocks } from '../../../app/components/PageLayout';
import { FeaturedCountry } from '../../services/resources/ResourceService';
import BlogPostInfo from '../BlogPostInfo/BlogPostInfo';
import Footer from '../Footer';
import { ImageMedia } from '../ImageMedia';
import { RichText } from '../RichText';
import FloatingCta from './FloatingCta';

const className = 'p-BlogPost';

type BlogPostProps = {
  post: Post;
  relatedPosts: Post[];
  plans: ProductOffering[];
  featuredCountries: FeaturedCountry[];
  countries: Country[];
  blockPlans: ProductOffering[];
  testimonials: TestimonialItem[];
  howDoesItWork: HowDoesItWork | null;
};

export const BlogPost = ({
  post,
  relatedPosts,
  plans,
  featuredCountries,
  countries,
  blockPlans,
  testimonials,
  howDoesItWork,
}: BlogPostProps) => {
  const { t } = useTranslation();

  const mainBodyContentTable: HeadingInfo[] =
    post.content?.root?.children?.flatMap((node: Node) => {
      const headingInfo = generateHeadingInfo(node);
      return headingInfo ? [headingInfo] : [];
    }) || [];

  const isRichTextBlock = (
    block: InfoBannerBlock | RichTextBlock | PlanCardsBlock,
  ): block is RichTextBlock => block.blockType === 'rich-text';

  const contentTableCombined = () => {
    const extractRichTextBlockHeadings = (
      block: RichTextBlock,
    ): HeadingInfo[] => {
      const children = block.content?.root?.children;
      if (!Array.isArray(children)) return [];

      return children
        .map((node: Node) => generateHeadingInfo(node))
        .filter(Boolean) as HeadingInfo[];
    };

    const richTextSections =
      (post.layout || [])
        .filter(isRichTextBlock)
        .flatMap((block) => extractRichTextBlockHeadings(block)) || [];

    return [...mainBodyContentTable, ...richTextSections];
  };

  return (
    <div className={className}>
      <div>
        <header className={`${className}__header`}>
          <div className={`${className}__header-image-container`}>
            <ImageMedia
              media={post.mainImage}
              className={`${className}__header-image`}
              sizes="(min-width: 768px) 50vw, 100vw"
              fetchpriority="high"
            />
          </div>
          <div className={`${className}__header-title`}>
            <h1 className={`${className}__header-heading`}>{post.title}</h1>
            <time>{formatDate(post.publishedAt, 'long')}</time>
          </div>
        </header>
        <div className={`${className}__content`}>
          {contentTableCombined().length > 0 && (
            <BlogPostInfo contentTable={contentTableCombined()} />
          )}
          <RichText content={post.content} data={{ plans }} />
          {post.layout && post.layout.length > 0 ? (
            <PageBlocks
              blocks={post.layout}
              plans={blockPlans}
              testimonials={testimonials}
              howDoesItWork={howDoesItWork}
            />
          ) : null}
        </div>
      </div>
      <LandingBlogPosts
        posts={relatedPosts}
        title={t('blog.more_articles', 'Related Articles')}
      />
      <FloatingCta floatingCta={post.floatingCta} />
      <Footer featuredCountries={featuredCountries} countries={countries} />
    </div>
  );
};

export default BlogPost;
